import { sortBy, keyBy, times } from 'lodash'
import { createSelector } from 'reselect'

import { t } from 'plottr_locales'

import { hierarchyLevel } from '../store/initialState'
import { outOfOrderSearch } from '../helpers/outOfOrderSearch'
import { reduce, depth, findNode, nodeParent, children, forEach, maxDepth } from '../reducers/tree'
import {
  beatsByPosition,
  rootParentId,
  beatTitle,
  numberOfPriorChildrenAtSameDepth,
  beatOneIsPrologue,
} from '../helpers/beats'
import { cardMapping } from '../helpers/cards'
import { sortLinesWithPinnedFirst } from '../helpers/lines'
import { createDeepEqualSelector } from './createDeepEqualSelector'

// Other selector dependencies
import { allLinesSelector } from './linesFirstOrder'
import {
  allCardMetaDataSelector,
  allCardsSelector,
  allCardIdAndKeyDataSelector,
} from './cardsFirstOrder'
import { beatsForBookOne, allBeatsSelector } from './beatsFirstOrder'
import { isDarkModeSelector } from './settingsFirstOrder'
import {
  attributesDialogIsOpenSelector,
  beatsByBookSelector,
  currentTimelineSelector,
  hierarchyLevelCount,
  isLargeSelector,
  isMediumSelector,
  isSmallSelector,
  sortedHierarchyLevels,
  timelineFilterIsEmptySelector,
  timelineFilterSelector,
  timelineScrollPositionSelector,
  timelineSearchTermSelector,
  timelineSelectedTabSelector,
  timelineSizeSelector,
  timelineViewIsntDefaultSelector,
  timelineViewIsStackedSelector,
  timelineViewIsTabbedSelector,
  uiSelector,
  hierarchyLevelSelector,
  hierarchyLevelsSelector,
  allHierarchyLevelsSelector,
} from './secondOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').Beat} Beat
 * @typedef {import('../index').Line} Line
 * @typedef {import('../index').Card} Card
 * @typedef {import('../index').HierarchyLevel} HierarchyLevel
 * @typedef {import('../index').CardMetadata} CardMetadata
 * @typedef {import('../index').PlottrFileBeatsEntry} Beats
 * @typedef {import('../index').TimelineBundle} TimelineBundle
 * @typedef {import('../index').MaybeTopLevelBeat} MaybeTopLevelBeat
 * @typedef {import('../index').TimelineOrientation} TimelineOrientation
 * @typedef {import('../index').PerBookHierarchyLevels} PerBookHierarchyLevels
 * @typedef {import('../types/plottr').TimelineFilter} TimelineFilter
 */

/**
 * @type {function(PlottrState): TimelineOrientation}
 */
const selectedOrientationSelector = createSelector(uiSelector, ({ orientation }) => {
  return orientation
})
/**
 * @type {function(PlottrState): TimelineOrientation}
 */
export const orientationSelector = createSelector(
  selectedOrientationSelector,
  isSmallSelector,
  timelineViewIsStackedSelector,
  (selectedOrientation, isSmall, timelineViewIsStacked) => {
    // Force horizontal view when we're on the stacked timeline.
    // Wehaven't thought through how flipped should look.
    if (selectedOrientation === 'vertical' && !isSmall && timelineViewIsStacked) {
      return 'horizontal'
    }
    return selectedOrientation
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const timelineIsExpandedSelector = createSelector(uiSelector, ({ timelineIsExpanded }) => {
  return timelineIsExpanded
})

/**
 * @type {function(PlottrState): TimelineBundle}
 */
export const timelineBundleSelector = createSelector(
  timelineScrollPositionSelector,
  currentTimelineSelector,
  orientationSelector,
  timelineSizeSelector,
  timelineIsExpandedSelector,
  isDarkModeSelector,
  attributesDialogIsOpenSelector,
  timelineFilterIsEmptySelector,
  isSmallSelector,
  isMediumSelector,
  isLargeSelector,
  (
    timelineScrollPosition,
    currentTimeline,
    orientation,
    timelineSize,
    timelineIsExpanded,
    darkMode,
    attributesDialogIsOpen,
    filterIsEmpty,
    isSmall,
    isMedium,
    isLarge
  ) => ({
    timelineScrollPosition,
    currentTimeline,
    orientation,
    timelineSize,
    timelineIsExpanded,
    darkMode,
    attributesDialogIsOpen,
    filterIsEmpty,
    isSmall,
    isMedium,
    isLarge,
  })
)

/**
 * @type {function(PlottrState): Array<Line>}
 */
export const linesByBookSelector = createSelector(
  allLinesSelector,
  currentTimelineSelector,
  (lines, bookId) => {
    return lines.filter((l) => l && l.bookId == bookId)
  }
)

/**
 * @type {function(PlottrState): Record<number, boolean>}
 */
export const lineIsExpandedSelector = createSelector(
  linesByBookSelector,
  timelineIsExpandedSelector,
  (lines, isExpanded) => {
    return lines.reduce((acc, l) => {
      // if line.expanded is null, then use timelineIsExpanded
      // else use line.expanded
      if (l.expanded == null) acc[l.id] = isExpanded
      else acc[l.id] = l.expanded

      return acc
    }, {})
  }
)

/**
 * @type {function(PlottrState): Array<Line>}
 */
export const sortedLinesByBookSelector = createSelector(
  linesByBookSelector,
  sortLinesWithPinnedFirst
)

/**
 * @type {function(PlottrState): Record<number, Line>}
 */
export const linesById = createSelector(sortedLinesByBookSelector, (lines) => {
  return keyBy(lines, 'id')
})

/**
 * @type {function(PlottrState): Record<number, Line>}
 */
export const linePositionMappingSelector = createSelector(linesByBookSelector, (lines) => {
  return lines.reduce((acc, line) => {
    acc[line.position] = line
    return acc
  }, {})
})

/**
 * @type {function(PlottrState): Array<Beat>}
 */
export const sortedBeatsByBookSelector = createSelector(
  beatsByBookSelector,
  beatsByPosition(() => true)
)

/**
 * @type {function(PlottrState): Map<number, number | Null>}
 */
export const collapsedBeatSelector = createSelector(
  beatsByBookSelector,
  sortedBeatsByBookSelector,
  (beatTree, sortedBeats) => {
    const collapsedBeats = new Map()
    const firstCollapsedParent = (beatId) => {
      if (!beatId) return null
      if (collapsedBeats.has(beatId)) {
        return collapsedBeats.get(beatId)
      }
      const directParentId = nodeParent(beatTree, beatId)
      const collapsedParentId = firstCollapsedParent(directParentId)
      if (collapsedParentId) {
        collapsedBeats.set(beatId, collapsedParentId)
        return collapsedParentId
      }
      const thisBeat = findNode(beatTree, beatId)
      if (!thisBeat.expanded) {
        collapsedBeats.set(beatId, beatId)
        return beatId
      }
      collapsedBeats.set(beatId, null)
      return null
    }
    sortedBeats.forEach((beat) => {
      firstCollapsedParent(beat.id)
    })
    return collapsedBeats
  }
)

const EMPTY_MAP = new Map()

/**
 * @type {function(PlottrState): Record<String, Array<Card>>}
 */
export const cardMapSelector = createSelector(
  allCardsSelector,
  collapsedBeatSelector,
  sortedBeatsByBookSelector,
  timelineViewIsntDefaultSelector,
  (cards, collapsedBeats, allSortedBeats, timelineViewIsntDefault) => {
    const beatIds = allSortedBeats.map(({ id }) => id)
    const beatPositions = beatIds.map((x) => x)
    beatIds.forEach((beatId, index) => (beatPositions[beatId] = index))
    return cards.reduce(
      cardReduce(
        'lineId',
        'beatId',
        !timelineViewIsntDefault ? collapsedBeats : EMPTY_MAP,
        beatPositions
      ),
      {}
    )
  }
)

/**
 * @type {function(PlottrState): Record<String, Array<Card>>}
 */
export const flatCardMapSelector = createSelector(
  allCardsSelector,
  collapsedBeatSelector,
  sortedBeatsByBookSelector,
  timelineViewIsntDefaultSelector,
  (cards, collapsedBeats, allSortedBeats, timelineViewIsntDefault) => {
    const hierarchyIsOn = false
    const beatIds = allSortedBeats.map(({ id }) => id)
    const beatPositions = beatIds.map((x) => x)
    beatIds.forEach((beatId, index) => (beatPositions[beatId] = index))
    return cards.reduce(
      cardReduce(
        'lineId',
        'beatId',
        hierarchyIsOn && !timelineViewIsntDefault ? collapsedBeats : EMPTY_MAP,
        beatPositions
      ),
      {}
    )
  }
)

/**
 * @type {function(PlottrState): Record<String, Array<Card>>}
 */
export const flatCardIdAndKeyDataMapSelector = createDeepEqualSelector(
  allCardIdAndKeyDataSelector,
  collapsedBeatSelector,
  sortedBeatsByBookSelector,
  timelineViewIsntDefaultSelector,
  (cards, collapsedBeats, allSortedBeats, timelineViewIsntDefault) => {
    const hierarchyIsOn = false
    const beatIds = allSortedBeats.map(({ id }) => id)
    const beatPositions = beatIds.map((x) => x)
    beatIds.forEach((beatId, index) => (beatPositions[beatId] = index))
    return cards.reduce(
      cardReduce(
        'lineId',
        'beatId',
        hierarchyIsOn && !timelineViewIsntDefault ? collapsedBeats : EMPTY_MAP,
        beatPositions
      ),
      {}
    )
  }
)

/**
 * @param {Beats} beats
 * @param {boolean} timelineViewIsTabbed
 * @return {Array<Beat>}
 */
const visibleBeatsByPosition = (beats, timelineViewIsTabbed) =>
  beatsByPosition(({ expanded }) => {
    return expanded || timelineViewIsTabbed
  })(beats).filter(({ id }) => {
    const currentDepth = depth(beats, id)
    return (timelineViewIsTabbed && currentDepth !== 0) || !timelineViewIsTabbed
  })

/**
 * @type {function(PlottrState): Array<Beat>}
 */
export const visibleSortedBeatsByBookSelector = createSelector(
  beatsByBookSelector,
  timelineViewIsTabbedSelector,
  visibleBeatsByPosition
)

/**
 * @type {function(Beats, boolean): Array<Beat>}
 */
const flatVisibleBeatsByPosition = (beats, timelineViewIsTabbed) => {
  const beatHierarchyIsOn = false
  return beatsByPosition(({ expanded }) => {
    return expanded || !beatHierarchyIsOn || timelineViewIsTabbed
  })(beats).filter(({ id }) => {
    const maximumDepth = maxDepth('id')(beats)
    const currentDepth = depth(beats, id)
    const beatIsVisible = beatHierarchyIsOn || currentDepth === maximumDepth
    return (
      (timelineViewIsTabbed && currentDepth !== 0 && beatIsVisible) ||
      (!timelineViewIsTabbed && beatIsVisible)
    )
  })
}

/**
 * @type {function(PlottrState): Array<Beat>}
 */
export const flatVisibleSortedBeatsByBookSelector = createSelector(
  beatsByBookSelector,
  timelineViewIsTabbedSelector,
  flatVisibleBeatsByPosition
)

/**
 * @type {function(PlottrState): Record<number, Array<Card>>}
 */
export const mobileOutlineCardMapSelector = createSelector(
  flatCardIdAndKeyDataMapSelector,
  flatVisibleSortedBeatsByBookSelector,
  sortedLinesByBookSelector,
  (card2DMap, chapters, lines) => {
    return cardMapping(chapters || [], lines, card2DMap, null)
  }
)

/**
 * @type {function(PlottrState): Record<number, number>}
 */
export const lineMaxCardsSelector = createSelector(
  linesByBookSelector,
  cardMapSelector,
  visibleSortedBeatsByBookSelector,
  (lines, cardMap, beats) => {
    return lines.reduce((acc, l) => {
      let max = 0
      beats.forEach((beat) => {
        const cards = cardMap[`${l.id}-${beat.id}`]
        if (cards && cards.length > max) max = cards.length
      })
      acc[l.id] = max
      return acc
    }, {})
  }
)

/**
 * @type {function(PlottrState): (function('series' | number): Beat)}
 */
export const firstVisibleBeatForBookThunkSelector = createSelector(
  allBeatsSelector,
  timelineViewIsTabbedSelector,
  (beats, timelineIsTabbed) => {
    return (bookId) => {
      const firstVisibleBeat = visibleBeatsByPosition(beats[bookId], timelineIsTabbed)[0]
      return firstVisibleBeat
    }
  }
)

/**
 * @param {PlottrState} _state
 * @param {'series' | number} bookId
 * @return {'series' | number}
 */
const bookIdSelector = (_state, bookId) => bookId
/**
 * @type {function(PlottrState, number | 'series'): (Beat | Null)}
 */
export const firstVisibleBeatForBookSelector = createSelector(
  allBeatsSelector,
  bookIdSelector,
  timelineViewIsTabbedSelector,
  (beats, bookId, timelineIsTabbed) => {
    const firstVisibleBeat = visibleBeatsByPosition(beats[bookId], timelineIsTabbed)[0]
    return firstVisibleBeat ?? null
  }
)

/**
 * @type {function(PlottrState): Record<number, number>}
 */
export const visibleBeatPositions = createSelector(
  beatsByBookSelector,
  timelineViewIsTabbedSelector,
  (beats, timelineViewIsTabbed) => {
    let lastRootIndex = 0
    let lastParentNodeId = null
    const reducer = timelineViewIsTabbed
      ? (acc, beat, index) => {
          const rootParentNodeId = rootParentId(beats, beat.id)
          if (lastParentNodeId !== rootParentNodeId) {
            lastRootIndex = index
            lastParentNodeId = rootParentNodeId
          }
          return {
            ...acc,
            [beat.id]: index - lastRootIndex,
          }
        }
      : (acc, beat, index) => {
          return {
            ...acc,
            [beat.id]: index,
          }
        }
    return visibleBeatsByPosition(beats, timelineViewIsTabbed).reduce(reducer, {})
  }
)

// This version ignores the view that the timeline is on so it's intended for the *Outline*.
/**
 * @type {function(PlottrState): Record<number, number>}
 */
export const sparceBeatMap = createSelector(beatsByBookSelector, (beats) =>
  visibleBeatsByPosition(beats, false).reduce((acc, beat, index) => {
    acc[index] = beat.id
    return acc
  }, {})
)

/**
 * @type {function(PlottrState): Array<Beat>}
 */
export const topTierBeatsInThreeTierArrangementSelector = createSelector(
  sortedHierarchyLevels,
  sortedBeatsByBookSelector,
  beatsByBookSelector,
  (hierarchyLevels, sortedBeats, beats) => {
    if (hierarchyLevels.length < 3) {
      return []
    }
    return sortedBeats.filter((beat) => {
      return depth(beats, beat.id) === 0
    })
  }
)

/**
 * @typedef {{ type: 'insert-placeholder' }} InsertPlaceholder
 */

/**
 * @type {function(PlottrState): Array<Beat | InsertPlaceholder>}
 */
export const secondTierBeatsInAtLeastTwoTierArrangementSelector = createSelector(
  sortedHierarchyLevels,
  sortedBeatsByBookSelector,
  beatsByBookSelector,
  topTierBeatsInThreeTierArrangementSelector,
  (hierarchyLevels, sortedBeats, beats, topTierBeats) => {
    if (hierarchyLevels.length < 2) {
      return []
    }
    const beatsAtSecondTier = sortedBeats.filter((beat) => {
      const currentDepth = depth(beats, beat.id)
      return (
        (hierarchyLevels.length === 3 && currentDepth === 1) ||
        (hierarchyLevels.length === 2 && currentDepth === 0)
      )
    })
    return beatsAtSecondTier.flatMap((beat, index) => {
      const currentDepth = depth(beats, beat.id)
      // Beat must be at the right level.
      if (currentDepth === 0) {
        return [beat]
      }
      const beatParent = nodeParent(beats, beat.id)
      const indexOfParentInParents = topTierBeats.findIndex(({ id }) => {
        return id === beatParent
      })
      // There must be a parent beat for this beat, that's not in position zero
      if (indexOfParentInParents > 0) {
        // How many palceholders do we add?
        //  - Count the number of beats at the parent level of the current
        //    beat that have no children.
        let closestIndexBackwardOfParentWithNoChildren
        for (
          closestIndexBackwardOfParentWithNoChildren = indexOfParentInParents - 1;
          closestIndexBackwardOfParentWithNoChildren > 0;
          closestIndexBackwardOfParentWithNoChildren--
        ) {
          const priorParent = topTierBeats[closestIndexBackwardOfParentWithNoChildren]
          const childrenOfPriorParent = children(beats, priorParent.id)
          // This is the closest beat back that has children
          if (childrenOfPriorParent && childrenOfPriorParent.length > 0) {
            break
          }
        }
        // If there was a beat with no children, then it was one
        // beat along.
        //
        // *But* only add placeholders for the first beat in the
        // next chapter that detects missing beats in the previous
        // chapter.
        const priorBeat = beatsAtSecondTier[index - 1]
        const priorBeatParent = priorBeat && nodeParent(beats, priorBeat.id)
        if (!(index === 0 && indexOfParentInParents > 0)) {
          closestIndexBackwardOfParentWithNoChildren++
        }
        if (
          closestIndexBackwardOfParentWithNoChildren < indexOfParentInParents &&
          priorBeatParent !== beatParent
        ) {
          // Insert placeholders for all of the missing beats
          /**
           * @type {InsertPlaceholder}
           */
          const placeholder = {
            type: 'insert-placeholder',
          }
          return [
            ...times(indexOfParentInParents - closestIndexBackwardOfParentWithNoChildren, () => {
              return placeholder
            }),
            beat,
          ]
        }
        return [beat]
      }
      return [beat]
    })
  }
)

/**
 * @param {PlottrState} _state
 * @param {number} beatId
 * @return {number}
 */
const beatIdSelector = (_state, beatId) => beatId
/**
 * @return {function(PlottrState, number): String}
 */
export const makeBeatTitleSelector = () => {
  /**
   * @type {function(PlottrState, number): String}
   */
  return createSelector(
    beatIndexSelector,
    beatsByBookSelector,
    beatIdSelector,
    sortedHierarchyLevels,
    positionOffsetSelector,
    (beatIndex, beats, beatId, hierarchyLevels, positionOffset) => {
      const beat = findNode(beats, beatId)
      if (!beat || typeof beatIndex !== 'number') return ''
      return beatTitle(beatIndex, beats, beat, hierarchyLevels, positionOffset)
    }
  )
}

/**
 * @return {function(PlottrState, number): String}
 */
export const makeFlatBeatTitleSelector = () =>
  /**
   * @type {function(PlottrState, number): String}
   */
  createSelector(
    beatIndexSelector,
    beatsByBookSelector,
    beatIdSelector,
    sortedHierarchyLevels,
    positionOffsetSelector,
    (beatIndex, beats, beatId, hierarchyLevels, positionOffset) => {
      const beat = findNode(beats, beatId)
      if (!beat || typeof beatIndex !== 'number') return ''
      return beatTitle(beatIndex, beats, beat, hierarchyLevels, positionOffset)
    }
  )

/**
 * @type {function(PlottrState): boolean}
 */
export const skipPrologueSelector = createSelector(sortedBeatsByBookSelector, (beats) =>
  beatOneIsPrologue(beats)
)

/**
 * @type {function(PlottrState): Array<String>}
 */
export const timelineTabsSelector = createSelector(
  beatsByBookSelector,
  sortedBeatsByBookSelector,
  sortedHierarchyLevels,
  skipPrologueSelector,
  (beats, sortedBeats, hierarchyLevels, skipPrologue) => {
    const atTopLevel = (beat) => {
      const currentDepth = depth(beats, beat.id)
      return currentDepth === 0
    }
    return beatsByPosition(atTopLevel)(beats)
      .filter(atTopLevel)
      .map((beat) => {
        const positionOffset = skipPrologue ? -1 : 0
        const beatNode = findNode(beats, beat.id)
        const beatIndex = numberOfPriorChildrenAtSameDepth(beats, sortedBeats, beat.id)
        if (typeof beatIndex === 'number') {
          return beatTitle(beatIndex, beats, beatNode, hierarchyLevels, positionOffset)
        } else {
          return t('Untitled')
        }
      })
  }
)

/**
 * @param {Beats} beats
 * @return {Array<Beat>}
 */
const visibleBeatsByPositionIgnoringCollapsed = (beats) =>
  beatsByPosition(() => {
    return true
  })(beats)
/**
 * @type {function(PlottrState): Array<Beat>}
 */
export const visibleSortedBeatsByBookIgnoringCollapsedSelector = createSelector(
  beatsByBookSelector,
  visibleBeatsByPositionIgnoringCollapsed
)

/**
 * @type {function(PlottrState): number}
 */
export const positionOffsetSelector = createSelector(skipPrologueSelector, (skip) =>
  skip ? -1 : 0
)

/**
 * @type {function(): function(PlottrState, number): Beat}
 */
export const makeBeatSelector = () => createSelector(beatsByBookSelector, beatIdSelector, findNode)

/**
 * @type {function(PlottrState): Map<number, boolean>}
 */
export const beatHasChildrenSelector = createSelector(beatsByBookSelector, (beats) => {
  return reduce('id')(
    beats,
    (beatsHaveChildren, beat) => {
      beatsHaveChildren.set(beat.id, (children(beats, beat.id) ?? []).length > 0)
      return beatsHaveChildren
    },
    new Map()
  )
})

/**
 * @type {function(PlottrState, number): (number | Null)}
 */
export const beatIndexSelector = createSelector(
  beatsByBookSelector,
  sortedBeatsByBookSelector,
  beatIdSelector,
  numberOfPriorChildrenAtSameDepth
)

/**
 * @type {function(PlottrState): Array<number>}
 */
export const timelineTabBeatIdsSelector = createSelector(beatsByBookSelector, (beats) => {
  const atTopLevel = (beat) => {
    const currentDepth = depth(beats, beat.id)
    return currentDepth === 0
  }
  return beatsByPosition(atTopLevel)(beats)
    .filter(atTopLevel)
    .map((beat) => {
      return beat.id
    })
})

/**
 * @type {function(PlottrState): number}
 */
export const timelineActiveTabSelector = createSelector(
  timelineSelectedTabSelector,
  timelineTabBeatIdsSelector,
  (tab, tabBeatIds) => {
    if (tabBeatIds.indexOf(tab) !== -1) {
      return tab
    }
    return tabBeatIds[0]
  }
)

/**
 * @param {Beats} beats
 * @param {boolean} timelineViewIsTabbed
 * @param {number} topLevelParentId
 * @param {boolean} timelineViewIsStacked
 * @param {boolean} timelineViewIsSmall
 * @param {number} hierarchyLevelCount
 * @return {Array<MaybeTopLevelBeat>}
 */
const visibleBeatsForTopLevelParentByPosition = (
  beats,
  timelineViewIsTabbed,
  topLevelParentId,
  timelineViewIsStacked,
  timelineViewIsSmall,
  hierarchyLevelCount
) => {
  const maximumDepth = hierarchyLevelCount - 1

  return beatsByPosition(({ expanded }) => {
    return expanded || timelineViewIsTabbed || timelineViewIsStacked
  })(beats)
    .filter(({ id }) => {
      const nodeChildren = children(beats, id) ?? []
      const currentDepth = depth(beats, id)
      const rootParentNodeId = rootParentId(beats, id)
      return (
        (timelineViewIsTabbed && rootParentNodeId === topLevelParentId && currentDepth !== 0) ||
        (timelineViewIsStacked && (timelineViewIsSmall || nodeChildren.length === 0)) ||
        (!timelineViewIsTabbed && !timelineViewIsStacked)
      )
    })
    .map((beat) => {
      const currentDepth = depth(beats, beat.id)
      if (!timelineViewIsSmall && timelineViewIsStacked && currentDepth !== maximumDepth) {
        return {
          ...beat,
          isInsertChildCell: true,
        }
      }
      return beat
    })
}

/**
 * @param {Beats} beats
 * @param {boolean} timelineViewIsTabbed
 * @param {number} activeTab
 * @param {boolean} timelineViewIsStacked
 * @param {boolean} timelineViewIsSmall
 * @param {number} hierarchyLevelCount
 * @return {Array<MaybeTopLevelBeat>}
 */
const visibleBeatsByPositionForTimeline = (
  beats,
  timelineViewIsTabbed,
  activeTab,
  timelineViewIsStacked,
  timelineViewIsSmall,
  hierarchyLevelCount
) => {
  const activeParentId = activeTab
  return visibleBeatsForTopLevelParentByPosition(
    beats,
    timelineViewIsTabbed,
    activeParentId,
    timelineViewIsStacked,
    timelineViewIsSmall,
    hierarchyLevelCount
  )
}

/**
 * @type {function(PlottrState): Array<MaybeTopLevelBeat>}
 */
export const visibleSortedBeatsForTimelineByBookSelector = createSelector(
  beatsByBookSelector,
  timelineViewIsTabbedSelector,
  timelineActiveTabSelector,
  timelineViewIsStackedSelector,
  isSmallSelector,
  hierarchyLevelCount,
  visibleBeatsByPositionForTimeline
)

/**
 * @type {function(PlottrState, number): Record<number, number>}
 */
export const timelineSparceBeatMap = createSelector(
  beatsByBookSelector,
  timelineViewIsTabbedSelector,
  timelineActiveTabSelector,
  timelineViewIsStackedSelector,
  isSmallSelector,
  hierarchyLevelCount,
  (beats, timelineViewIsTabbed, activeTab, timelineViewIsStacked, isSmall, levelCount) => {
    const activeParentId = activeTab

    return visibleBeatsForTopLevelParentByPosition(
      beats,
      timelineViewIsTabbed,
      activeParentId,
      timelineViewIsStacked,
      isSmall,
      levelCount
    ).reduce((acc, beat, index) => {
      acc[index] = beat.id
      return acc
    }, {})
  }
)

// We're sumonning the full state in order to call a selector on it
// later.
/**
 * @param {PlottrState} state
 * @return {PlottrState}
 */
const rawUnsafeFullStateSelector = (state) => state
/**
 * @type {function(PlottrState): Array<HierarchyLevel>}
 */
export const sortedBeatsHierachyLevels = createSelector(
  sortedBeatsByBookSelector,
  rawUnsafeFullStateSelector,
  (beats, state) => {
    return beats.map((beat) => {
      return hierarchyLevelSelector(
        state,
        // @ts-ignore
        beat.id
      )
    })
  }
)

/**
 * Produce the width that the tree stretches beneath the beat of the
 * given id.
 *
 * @type {function(PlottrState): Map<number, number>}
 */
export const leavesPerBeatSelector = createSelector(beatsByBookSelector, (beats) => {
  const index = new Map()
  const leavesForBeat = (id) => {
    const currentCount = index.get(id)
    if (currentCount) {
      return currentCount
    }

    const nodeChildren = children(beats, id)
    if (!nodeChildren || nodeChildren?.length === 0) {
      return 1
    }

    return nodeChildren
      .map(({ id }) => id)
      .map(leavesForBeat)
      .reduce((acc, childCount) => {
        return acc + childCount
      }, 0)
  }

  forEach(beats, ({ id }) => {
    index.set(id, leavesForBeat(id))
  })
  return index
})

/**
 * @type {function(PlottrState): boolean}
 */
export const cardsExistOnTimelineSelector = createSelector(
  allCardMetaDataSelector,
  visibleSortedBeatsByBookIgnoringCollapsedSelector,
  (allCardsMetaData, visibleSortedBeats) => {
    return (
      allCardsMetaData.filter(({ beatId }) => visibleSortedBeats.find(({ id }) => id === beatId))
        .length > 0
    )
  }
)

/**
 * @type {function(PlottrState): Record<number, String>}
 */
const stringifiedCardsByIdWhenSearchedSelector = createSelector(
  allCardsSelector,
  timelineSearchTermSelector,
  (cards, searchTerm) => {
    if (searchTerm && typeof searchTerm === 'string' && searchTerm !== '') {
      return cards.reduce((acc, nextCard) => {
        return {
          ...acc,
          [nextCard.id]: JSON.stringify(nextCard).toLowerCase(),
        }
      }, {})
    } else {
      return {}
    }
  }
)

/**
 * @type {function(PlottrState): Record<String, Array<CardMetadata>>}
 */
export const cardMetaDataMapSelector = createDeepEqualSelector(
  allCardMetaDataSelector,
  collapsedBeatSelector,
  sortedBeatsByBookSelector,
  (cards, collapsedBeats, allSortedBeats) => {
    const beatIds = allSortedBeats.map(({ id }) => id)
    const beatPositions = beatIds.map((x) => x)
    beatIds.forEach((beatId, index) => (beatPositions[beatId] = index))
    return cards.reduce(cardMetadataReduce('lineId', 'beatId', collapsedBeats, beatPositions), {})
  }
)

/**
 * @type {function(PlottrState): Record<String, Array<CardMetadata>>}
 */
export const _searchedCardMetaDataMapSelector = createDeepEqualSelector(
  allCardMetaDataSelector,
  collapsedBeatSelector,
  sortedBeatsByBookSelector,
  timelineSearchTermSelector,
  stringifiedCardsByIdWhenSearchedSelector,
  beatsByBookSelector,
  timelineViewIsStackedSelector,
  hierarchyLevelCount,
  timelineViewIsntDefaultSelector,
  (
    cards,
    collapsedBeats,
    allSortedBeats,
    timelineSearchTerm,
    stringifiedCards,
    beats,
    timelineViewIsStacked,
    hierarchyLevelCount,
    timelineViewIsntDefault
  ) => {
    const beatIds = allSortedBeats.map(({ id }) => id)
    const beatPositions = beatIds.map((x) => x)
    beatIds.forEach((beatId, index) => (beatPositions[beatId] = index))
    const lowerCaseSearchTerms =
      timelineSearchTerm &&
      timelineSearchTerm
        .toLowerCase()
        .split(' ')
        .filter((x) => x)

    const filteredCards = (
      lowerCaseSearchTerms
        ? cards.filter(({ id }) => {
            return outOfOrderSearch(lowerCaseSearchTerms, stringifiedCards[id])
          })
        : cards
    ).filter(({ beatId }) => {
      if (!beatIds.includes(beatId)) {
        return false
      } else {
        const beatIsALeaf = depth(beats, beatId) === hierarchyLevelCount - 1
        return !timelineViewIsStacked || beatIsALeaf
      }
    })

    return filteredCards.reduce(
      cardMetadataReduce(
        'lineId',
        'beatId',
        !timelineViewIsntDefault ? collapsedBeats : EMPTY_MAP,
        beatPositions
      ),
      {}
    )
  }
)
/**
 * @type {function(PlottrState): Record<String, Array<CardMetadata>>}
 */
export const searchedCardMetaDataMapSelector = createDeepEqualSelector(
  _searchedCardMetaDataMapSelector,
  (cardMap) => {
    return cardMap
  }
)

/**
 * @type {function(PlottrState): Record<number, boolean>}
 */
export const visibleCardsSelector = createSelector(
  allCardsSelector,
  timelineFilterSelector,
  timelineFilterIsEmptySelector,
  (cards, filter, filterIsEmpty) => {
    return cards.reduce((acc, c) => {
      acc[c.id] = filter === null || cardIsVisible(c, filter, filterIsEmpty)
      return acc
    }, {})
  }
)

/**
 * @param {String} lineAttr
 * @param {String} beatAttr
 * @param {Map<number, number | Null>} collapsedBeats
 * @param {Record<number, number>} beatPositions
 * @return {function(Record<String, Array<Card>>, Card): Record<String, Array<Card>>}
 */
function cardReduce(lineAttr, beatAttr, collapsedBeats, beatPositions) {
  /**
   * @param {Record<String, Array<Card>>} acc
   * @param {Card} card
   * @return {Record<String, Array<Card>>}
   */
  return (acc, card) => {
    const cardBeatId = card[beatAttr]
    const beatId = (collapsedBeats && collapsedBeats.get(cardBeatId)) || cardBeatId
    const val = acc[`${card[lineAttr]}-${beatId}`]
    if (val && val.length) {
      const cards = [...val, card]
      const sortedCards = sortBy(sortBy(cards, 'positionWithinLine'), (c) => {
        const bId = c[beatAttr]
        return beatPositions[bId]
      })
      acc[`${card[lineAttr]}-${beatId}`] = sortedCards
    } else {
      acc[`${card[lineAttr]}-${beatId}`] = [card]
    }

    return acc
  }
}

/**
 * @param {String} lineAttr
 * @param {String} beatAttr
 * @param {Map<number, number | Null>} collapsedBeats
 * @param {Record<number, number>} beatPositions
 * @return {function(Record<String, Array<CardMetadata>>, CardMetadata): Record<String, Array<CardMetadata>>}
 */
function cardMetadataReduce(lineAttr, beatAttr, collapsedBeats, beatPositions) {
  /**
   * @param {Record<String, Array<CardMetadata>>} acc
   * @param {CardMetadata} card
   * @return {Record<String, Array<CardMetadata>>}
   */
  return (acc, card) => {
    const cardBeatId = card[beatAttr]
    const beatId = (collapsedBeats && collapsedBeats.get(cardBeatId)) || cardBeatId
    const val = acc[`${card[lineAttr]}-${beatId}`]
    if (val && val.length) {
      const cards = [...val, card]
      const sortedCards = sortBy(sortBy(cards, 'positionWithinLine'), (c) => {
        const bId = c[beatAttr]
        return beatPositions[bId]
      })
      acc[`${card[lineAttr]}-${beatId}`] = sortedCards
    } else {
      acc[`${card[lineAttr]}-${beatId}`] = [card]
    }

    return acc
  }
}

/**
 * @param {Card} card
 * @param {TimelineFilter} filter
 * @param {boolean} filterIsEmpty
 * @return {boolean}
 */
function cardIsVisible(card, filter, filterIsEmpty) {
  if (filterIsEmpty) return true

  return Object.keys(filter).some((attr) => {
    return filter[attr].some((val) => {
      if (card[attr] !== undefined) {
        return card[attr] === val
      }
      if (!val && typeof val !== 'number' && !card[attr]) {
        return true
      }
      if (typeof val === 'number') {
        if (attr == 'tag') {
          return card.tags.includes(val)
        }
        if (attr == 'character') {
          return card.characters.includes(val)
        }
        if (attr == 'place') {
          return card.places.includes(val)
        }
      }
      return false
    })
  })
}

/**
 * @type {function(PlottrState, number): boolean}
 */
export const parentIsHigherLevelAndViewIsStackedSelector = createSelector(
  timelineViewIsStackedSelector,
  hierarchyLevelSelector,
  sortedHierarchyLevels,
  (timelineIsStackedView, hierarchyLevel, sortedLevels) => {
    return timelineIsStackedView && hierarchyLevel.level !== sortedLevels.length - 1
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const canOpenRestructureModalSelector = createSelector(beatsByBookSelector, (beats) => {
  return Object.keys(beats.index || {}).length > 1
})

/**
 * @type {function(PlottrState): number}
 */
export const bottomLevelBeatHeadingCountSelector = createSelector(
  visibleSortedBeatsForTimelineByBookSelector,
  (beats) => {
    return beats.length
  }
)

/**
 * @type {function(PlottrState): Array<HierarchyLevel>}
 */
export const sortedHierarchyLevelsOrDefault = createSelector(
  hierarchyLevelCount,
  hierarchyLevelsSelector,
  (levels, rawHierarchyLevels) => {
    const sortedLevels = []
    const hierarchyLevels = rawHierarchyLevels ?? { 0: hierarchyLevel() }
    for (let i = 0; i < levels; ++i) {
      sortedLevels.push(hierarchyLevels[i])
    }
    return sortedLevels
  }
)

/**
 * @type {function(PlottrState): PerBookHierarchyLevels}
 */
export const hierarchyLevelsForBookOneSelector = createSelector(
  allHierarchyLevelsSelector,
  (allLevels) => {
    return allLevels[1]
  }
)

/**
 * @type {function(PlottrState): number}
 */
export const hierarchyLevelCountForBookOne = createSelector(
  hierarchyLevelsForBookOneSelector,
  (hierarchyLevels) => {
    return hierarchyLevels ? Object.keys(hierarchyLevels).length : 1
  }
)

/**
 * @type {function(PlottrState): Array<HierarchyLevel>}
 */
export const sortedHierarchyLevelsForBookOneOrDefault = createSelector(
  hierarchyLevelCountForBookOne,
  hierarchyLevelsForBookOneSelector,
  (levels, rawHierarchyLevels) => {
    const sortedLevels = []
    const hierarchyLevels = rawHierarchyLevels ?? { 0: hierarchyLevel() }
    for (let i = 0; i < levels; ++i) {
      sortedLevels.push(hierarchyLevels[i])
    }
    return sortedLevels
  }
)

/**
 * @type {function(PlottrState): Array<Beat>}
 */
export const templateBeatsForBookOne = createSelector(
  beatsForBookOne,
  sortedHierarchyLevelsForBookOneOrDefault,
  positionOffsetSelector,
  (beatTree, hierarchyLevels, indexOffset) => {
    const sortedBeats = beatsByPosition(() => true)(beatTree)
    return sortedBeats.map((beat, index) => {
      return {
        ...beat,
        title: beatTitle(index + 1 + indexOffset, beatTree, beat, hierarchyLevels, 0),
      }
    })
  }
)
